import { map, forEach, every, some, isEqual } from 'lodash'


export const validateField = async (validators, stateValue, stateValues) => {
  async function iterate(index) {
    if (!validators[index]) {
      return
    }

    const error = await validators[index](stateValue, stateValues)

    if (error || index === validators.length - 1) {
      return error
    }

    return iterate(++index)
  }

  return iterate(0)
}

const validateGroupFields = async (fieldOpts, stateValues, isGroupChanged) => {
  const result = {}
  const fieldNames = Object.keys(fieldOpts)

  const resultMap = await Promise.all(fieldNames.map(async (fieldName) => {
    const _fieldOpts = fieldOpts[fieldName]

    if (_fieldOpts.fields) {
      return validateGroupFields(_fieldOpts.fields, stateValues[fieldName], isGroupChanged)
    }

    if (!isGroupChanged) {
      return { error: undefined, validate: false }
    }

    const error = await validateField(_fieldOpts.validate, stateValues[fieldName], stateValues)

    return { error, validate: true }
  }))

  fieldNames.forEach((fieldName, index) => {
    result[fieldName] = resultMap[index]
  })

  return result
}

const validateFields = async (fieldOpts, stateValues) => {
  const result = {}
  const fieldNames = Object.keys(fieldOpts)

  const resultMap = await Promise.all(fieldNames.map(async (fieldName) => {
    const _fieldOpts = fieldOpts[fieldName]

    if (_fieldOpts.fields) {
      return validateFields(_fieldOpts.fields, stateValues[fieldName])
    }

    const error = await validateField(_fieldOpts.validate, stateValues[fieldName], stateValues)

    return { error, validate: true }
  }))

  fieldNames.forEach((fieldName, index) => {
    result[fieldName] = resultMap[index]
  })

  return result
}

const checkFormValid = (fields) =>
  every(fields, (fieldOpts, fieldName) => {
    if ('error' in fieldOpts) {
      return !Boolean(fieldOpts.error)
    }
    return checkFormValid(fields[fieldName])
  })


const checkFieldFilled = (fieldOpts, fieldValue) => {
  if (fieldOpts.fields) {
    return some(fieldOpts.fields, (fieldOpts, fieldName) => checkFieldFilled(fieldOpts, fieldValue[fieldName]))
  }
  if (fieldOpts.readOnly) {
    return false
  }
  return Boolean(fieldValue)
}

const checkGroupFilled = (groupFieldNames, fieldOpts, stateValues) =>
  some(groupFieldNames, (groupFieldName) => checkFieldFilled(fieldOpts[groupFieldName], stateValues[groupFieldName]))

const checkGroupChanged = (groupFieldNames, stateValues, initialValues) =>
  some(groupFieldNames, (fieldName) => !isEqual(stateValues[fieldName], initialValues[fieldName]))

const validateGroup = async ({ fieldNames, fieldOpts, stateValues, isChanged }) => {
  const result = {}

  await Promise.all(map(fieldNames, async (fieldName) => {
    const value = stateValues[fieldName]
    let opts = fieldOpts[fieldName]
    opts = opts.fields || opts

    result[fieldName] = await validateGroupFields(opts, value, isChanged)
  }))

  return result
}

const validateGroups = async (groups, fieldOpts, stateValues, initialValues) => {
  const processedGroups = map(groups, ({ fields: groupFieldNames, isRequired }) => ({
    fieldNames: groupFieldNames,
    isFilled: checkGroupFilled(groupFieldNames, fieldOpts, stateValues),
    isChanged: isRequired ? true : checkGroupChanged(groupFieldNames, stateValues, initialValues),
  }))

  const validated = await Promise.all(map(processedGroups, async ({ fieldNames, isChanged }) =>
    validateGroup({
      fieldNames,
      fieldOpts,
      stateValues,
      isChanged,
    })
  ))

  await Promise.all(map(processedGroups, async ({ isChanged }, index) => {
    if (isChanged && groups[index].validate) {
      await Promise.all(map(groups[index].validate, async (groupIndex) => {
        validated[groupIndex] = await validateGroup({
          fieldNames: processedGroups[groupIndex].fieldNames,
          fieldOpts,
          stateValues,
          isChanged: true,
        })
      }))
    }
  }))

  let validatedFields = {}

  forEach([].concat(...validated), (fields) => {
    validatedFields = {
      ...validatedFields,
      ...fields,
    }
  })

  return {
    validatedFields,
    groups: processedGroups,
  }
}


const validateForm = async (options, stateValues, initialValues) => {
  const { fields: fieldOpts, validateGroups: groups } = options
  let result = {}

  if (groups) {
    result = await validateGroups(groups, fieldOpts, stateValues, initialValues)
  }
  else {
    const validatedFields = await validateFields(fieldOpts, stateValues)

    result = {
      validatedFields,
    }
  }

  result = {
    ...result,
    isFormValid: checkFormValid(result.validatedFields),
  }

  return result
}

export default validateForm
